const extractLineValue = (text, pattern) => {
  const match = text.match(pattern);
  return match ? match[1].trim() : null;
};

export const validateStepResponse = (stepId, text, { expectedLatest = null } = {}) => {
  const t = String(text || '');

  if (t.includes('comando: erro') && !t.includes('[OK]') && stepId !== 6) {
    return { ok: false, reason: 'Equipamento retornou erro sem detalhe' };
  }

  switch (stepId) {
    case 1: {
      if (!/fetch exit: 0/.test(t)) {
        return { ok: false, reason: 'Git fetch falhou' };
      }
      const latest = t.match(/Ultima versao: (SW\.MT[^\s]+)/)?.[1];
      if (!latest) {
        return { ok: false, reason: 'Ultima versao vazia ou inválida' };
      }
      return { ok: true, latest };
    }
    case 2:
      if (
        !/\[OK\] sem index\.lock/.test(t)
        && /index\.lock/.test(t)
        && !/No such file|cannot access/i.test(t)
      ) {
        return { ok: false, reason: 'index.lock ainda presente' };
      }
      return { ok: true };
    case 3:
      if (!/\[OK\] reset\/clean/.test(t)) {
        return { ok: false, reason: 'Reset/clean falhou' };
      }
      return { ok: true };
    case 4:
      if (/\[FALHA\]/.test(t)) {
        return { ok: false, reason: 'Checkout sw-mt falhou' };
      }
      if (!/\[OK\] sw-mt atualizado/.test(t)) {
        return { ok: false, reason: 'Versão não confirmada' };
      }
      return { ok: true };
    case 5:
      if (/\[OK\] stable nao existe, pulando/.test(t)) {
        return { ok: true, skipped: true };
      }
      if (/\[FALHA\] stable/.test(t)) {
        return { ok: false, reason: 'Stable falhou' };
      }
      if (!/\[OK\] stable/.test(t)) {
        return { ok: false, reason: 'Stable não confirmou [OK]' };
      }
      return { ok: true };
    case 6:
      return {
        ok: true,
        warn: t.includes('comando: erro')
          ? 'PM2 pode ter exit ≠ 0; confirme no passo 9'
          : null,
      };
    case 7:
      return {
        ok: /\[OK\] Curvas limpas/.test(t),
        reason: /\[OK\] Curvas limpas/.test(t) ? null : 'Limpeza de curvas falhou',
      };
    case 9: {
      const ver = t.match(/Versao sw-mt: (SW\.MT[^\s]+)/)?.[1];
      const stable = t.match(/Versao sw-mt-stable: (SW\.MT[^\s]+)/)?.[1];
      if (!ver) {
        return { ok: false, reason: 'Versão sw-mt não legível' };
      }
      if (stable && stable !== ver) {
        return { ok: false, reason: 'sw-mt e stable divergem' };
      }
      if (expectedLatest && ver !== expectedLatest) {
        return {
          ok: false,
          reason: `Versão instalada (${ver}) difere da alvo (${expectedLatest})`,
        };
      }
      return { ok: true, version: ver };
    }
    default:
      return { ok: true };
  }
};

export const parseEquipmentUpdateOutput = (output, stepId, options = {}) => {
  const text = String(output || '');
  const { expectedLatest = null, abortOnFailure = true } = options;

  const validation = validateStepResponse(stepId, text, { expectedLatest });

  const result = {
    status: validation.ok ? 'success' : 'error',
    messages: [],
    shouldAbort: !validation.ok && abortOnFailure !== false,
    meta: {},
    validation,
  };

  if (validation.reason) {
    result.messages.push(validation.reason);
  }
  if (validation.warn) {
    result.status = 'warning';
    result.messages.push(validation.warn);
    result.shouldAbort = false;
  }
  if (validation.skipped) {
    result.status = 'warning';
    result.messages.push('Repositório stable não existe — passo ignorado.');
    result.shouldAbort = false;
  }

  if (/\[FALHA\]/i.test(text) && validation.ok === false) {
    result.status = 'error';
    if (!validation.reason) {
      result.messages.push('Falha reportada pelo equipamento.');
    }
  }

  if (/\[OK\]/i.test(text) && result.status !== 'error') {
    result.messages.push('Operação concluída com sucesso.');
  }

  result.meta.fetchExit = extractLineValue(text, /fetch exit:\s*(\d+)/i);
  result.meta.resetExit = extractLineValue(text, /reset exit:\s*(\d+)/i);
  result.meta.checkoutExit = extractLineValue(text, /checkout exit:\s*(\d+)/i);
  result.meta.currentVersion = extractLineValue(text, /Versao(?: atual)?:\s*(\S+)/i);
  result.meta.latestVersion = validation.latest
    || extractLineValue(text, /Ultima versao(?: Git)?:\s*(\S+)/i);
  result.meta.applyingVersion = extractLineValue(text, /Aplicando:\s*(SW\.MT[^\s]+)/i);
  result.meta.swMtUpdated = /\[OK\]\s*sw-mt atualizado/i.test(text);
  result.meta.stableSkipped = /\[OK\]\s*stable nao existe, pulando/i.test(text);
  result.meta.stableOk = /\[OK\]\s*stable/i.test(text) && !result.meta.stableSkipped;
  result.meta.confirmedVersion = validation.version || null;

  const isSwMtVersion = (version) => /^SW\.MT/i.test(String(version || ''));

  if (
    stepId === 1
    && isSwMtVersion(result.meta.currentVersion)
    && isSwMtVersion(result.meta.latestVersion)
    && result.meta.currentVersion === result.meta.latestVersion
    && validation.ok
  ) {
    result.status = 'warning';
    result.messages.push('Equipamento já está na última versão detectada (continuando).');
    result.shouldAbort = false;
  }

  if (/index\.lock:\s*File exists/i.test(text)) {
    result.messages.push('Lock detectado — repita o passo 2 (remover index.lock).');
  }

  if (stepId === 6 && /\[OK\]\s*pm2 restart enviado/i.test(text)) {
    result.status = validation.warn ? 'warning' : 'success';
    result.shouldAbort = false;
    result.messages.push('Comando PM2 enviado — aguarde alguns segundos.');
  }

  result.canAdvance = !result.shouldAbort;
  return result;
};

export const canAdvanceAfterResponse = (parsed) => parsed?.canAdvance !== false;

export const getStepStatusClass = (status, isDark) => {
  if (status === 'success') return 'text-success';
  if (status === 'error') return 'text-danger';
  if (status === 'warning') return isDark ? 'text-warning' : 'text-warning';
  if (status === 'running') return isDark ? 'text-info' : 'text-primary';
  return isDark ? 'text-light opacity-75' : 'text-muted';
};
